import { datadogRum } from "@datadog/browser-rum";
import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Outermost error boundary that catches errors from provider components
 * (Redux Provider, DndProvider, ThemeProvider, etc.) that sit above the
 * inner AppErrorBoundary. Without this, those errors would cause an
 * unrecoverable white screen.
 */
class TopLevelErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("TopLevelErrorBoundary caught an error:", error, errorInfo);
    try {
      datadogRum.addError(error, {
        source: "TopLevelErrorBoundary",
        componentStack: errorInfo.componentStack,
      });
    } catch {
      // Datadog may not be initialized yet
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            fontFamily: "system-ui, sans-serif",
            gap: "12px",
          }}
        >
          <span>An error has occurred</span>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: "8px 16px",
              cursor: "pointer",
            }}
          >
            Reload page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default TopLevelErrorBoundary;
